import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import moment from 'moment';
import 'moment/locale/pt-br';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import * as S from './styles';
import { TitlePage } from '../../../components/newStudent/Title/TitlePage';
import Divider from '../../../components/common/Divider';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../components/newStudent/ProviderNewStudent';
import {
  calendarServiceNewStudent,
  type EvaluativeCalendarEvent,
} from '../../../services/newStudent';
import { AletNotfoundLoading } from '../../../components/newStudent/Alert';

moment.locale('pt-br');
moment.updateLocale('pt-br', { week: { dow: 0 } });

const WEEKDAY_LETTERS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const GRID_DAYS = 42;

type CalendarViewMode = 'month' | 'list';

function startOfCalendarGrid(month: moment.Moment): moment.Moment {
  const first = month.clone().startOf('month');
  const dow = first.day();
  return first.clone().subtract(dow, 'days');
}

export default function CalendarNewStudent() {
  const theme = useTheme();
  const calendarDark = S.isCalendarDarkTheme(theme);

  const { setIsLoading } = useContext(NewStudentProviderContext) as INewStudentProvider;

  const [visibleMonth, setVisibleMonth] = useState(() => moment().startOf('month'));
  const [events, setEvents] = useState<EvaluativeCalendarEvent[]>([]);
  const [courses, setCourses] = useState<{ id: number; courseName: string }[]>([]);
  const [courseFilter, setCourseFilter] = useState<string>('');
  const [viewMode, setViewMode] = useState<CalendarViewMode>('month');
  const [dayModal, setDayModal] = useState<{
    dateKey: string;
    events: EvaluativeCalendarEvent[];
  } | null>(null);

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      const courseId =
        courseFilter === '' ? undefined : Number.parseInt(courseFilter, 10);
      const { result, data } =
        await calendarServiceNewStudent.evaluativeCalendar(
          Number.isFinite(courseId) ? courseId : undefined,
        );
      if (result && data) {
        setEvents(data.events ?? []);
        setCourses(data.courses ?? []);
      } else {
        setEvents([]);
        setCourses([]);
      }
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [courseFilter]);

  const eventsByDate = useMemo(() => {
    const map: Record<string, EvaluativeCalendarEvent[]> = {};
    for (const ev of events) {
      if (!ev.date) continue;
      if (!map[ev.date]) map[ev.date] = [];
      map[ev.date].push(ev);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => a.title.localeCompare(b.title));
    }
    return map;
  }, [events]);

  const gridStart = useMemo(
    () => startOfCalendarGrid(visibleMonth),
    [visibleMonth],
  );

  const cells = useMemo(() => {
    const list: { date: moment.Moment; inMonth: boolean }[] = [];
    for (let i = 0; i < GRID_DAYS; i++) {
      const d = gridStart.clone().add(i, 'days');
      list.push({
        date: d,
        inMonth: d.month() === visibleMonth.month(),
      });
    }
    return list;
  }, [gridStart, visibleMonth]);

  const listAgendaGroups = useMemo(() => {
    const y = visibleMonth.year();
    const m = visibleMonth.month();
    const inMonth = events.filter((e) => {
      if (!e.date) return false;
      const d = moment(e.date, 'YYYY-MM-DD', true);
      return d.isValid() && d.year() === y && d.month() === m;
    });
    inMonth.sort((a, b) => {
      const byDate = a.date.localeCompare(b.date);
      return byDate !== 0 ? byDate : a.title.localeCompare(b.title);
    });
    const groups: [string, EvaluativeCalendarEvent[]][] = [];
    let chunk: EvaluativeCalendarEvent[] = [];
    let key = '';
    for (const ev of inMonth) {
      if (ev.date !== key) {
        if (key) groups.push([key, chunk]);
        key = ev.date;
        chunk = [ev];
      } else {
        chunk.push(ev);
      }
    }
    if (key) groups.push([key, chunk]);
    return groups;
  }, [events, visibleMonth]);

  const prevMonth = () => setVisibleMonth((m) => m.clone().subtract(1, 'month'));
  const nextMonth = () => setVisibleMonth((m) => m.clone().add(1, 'month'));

  const prevLabel = visibleMonth.clone().subtract(1, 'month').format('MMMM [de] YYYY');
  const nextLabel = visibleMonth.clone().add(1, 'month').format('MMMM [de] YYYY');
  const centerLabel = visibleMonth.format('MMMM [de] YYYY');

  const todayKey = moment().format('YYYY-MM-DD');

  const modalMoment = dayModal
    ? moment(dayModal.dateKey, 'YYYY-MM-DD', true)
    : null;

  return (
    <S.Container>
      <TitlePage
        title="Calendário"
        titlePage="Calendário"
        subtitle="Visualize as datas de início e término das avaliações das suas disciplinas (graduação — portal novo)."
      />

      <Divider padding />

      <S.Content>
        {courses.length === 0 && events.length === 0 ? (
          <>
            <Divider padding />
            <AletNotfoundLoading toRender />
          </>
        ) : (
          <S.CalendarCard>
            <S.Toolbar>
              <S.ToolbarLeft>
                <S.SelectField
                  aria-label="Curso"
                  value={courseFilter}
                  onChange={(e) => setCourseFilter(e.target.value)}
                >
                  <option value="">Todos os cursos</option>
                  {courses.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.courseName}
                    </option>
                  ))}
                </S.SelectField>

                <S.SelectField
                  aria-label="Visualização"
                  value={viewMode}
                  onChange={(e) =>
                    setViewMode(e.target.value as CalendarViewMode)
                  }
                >
                  <option value="month">Mês</option>
                  <option value="list">Lista</option>
                </S.SelectField>
              </S.ToolbarLeft>
            </S.Toolbar>

            <S.MonthNav>
              <S.MonthTitle>{centerLabel}</S.MonthTitle>
              <S.MonthNavRow>
                <S.NavLink type="button" onClick={prevMonth}>
                  ◀ {prevLabel}
                </S.NavLink>
                <S.NavLink type="button" $alignEnd onClick={nextMonth}>
                  {nextLabel} ▶
                </S.NavLink>
              </S.MonthNavRow>
            </S.MonthNav>

            {viewMode === 'month' ? (
              <>
                <S.GridMonthRow>
                  <S.MonthAbbrev>
                    {visibleMonth.format('MMM').toUpperCase()}.
                  </S.MonthAbbrev>
                  <S.MonthAbbrevYear>{visibleMonth.format('YYYY')}</S.MonthAbbrevYear>
                </S.GridMonthRow>

                <S.GridScroll>
                  <S.Grid>
                    {WEEKDAY_LETTERS.map((letter, i) => (
                      <S.WeekdayHeader key={letter + String(i)} $sunday={i === 0}>
                        {letter}
                      </S.WeekdayHeader>
                    ))}
                    {cells.map(({ date, inMonth }) => {
                      const key = date.format('YYYY-MM-DD');
                      const dayEvents = eventsByDate[key] ?? [];
                      const isToday = key === todayKey;
                      const hasActivity = dayEvents.length > 0;
                      const bars = dayEvents.slice(0, 3);
                      const isSelected = dayModal?.dateKey === key;

                      const inner = (
                        <>
                          <S.DayNumberWrap $isToday={isToday} $isSelected={isSelected}>
                            {date.date()}
                          </S.DayNumberWrap>
                          {hasActivity ? (
                            <>
                              <S.ActivityBars>
                                {bars.map((ev) => (
                                  <S.ActivityBarSegment
                                    key={ev.id}
                                    $variant={ev.kind}
                                  />
                                ))}
                              </S.ActivityBars>
                              <S.DesktopEventsStack>
                                {bars.map((ev) => (
                                  <S.DesktopEventPill
                                    key={ev.id}
                                    $kind={ev.kind}
                                    title={`${ev.title}\n${ev.disciplineName ?? ''}\n${ev.courseName ?? ''}`}
                                  >
                                    <S.DesktopEventIcon aria-hidden />
                                    <S.DesktopEventPillText>{ev.title}</S.DesktopEventPillText>
                                  </S.DesktopEventPill>
                                ))}
                              </S.DesktopEventsStack>
                            </>
                          ) : null}
                        </>
                      );

                      const clickable = inMonth || hasActivity;

                      return clickable ? (
                        <S.DayCellButton
                          key={key}
                          type="button"
                          $muted={!inMonth}
                          $hasActivity={hasActivity}
                          aria-label={`Dia ${date.format('D [de] MMMM')}, ${dayEvents.length} atividade(s). Abrir detalhes.`}
                          onClick={() =>
                            setDayModal({ dateKey: key, events: dayEvents })
                          }
                        >
                          {inner}
                        </S.DayCellButton>
                      ) : (
                        <S.DayCell key={key} $muted $hasActivity={false}>
                          {inner}
                        </S.DayCell>
                      );
                    })}
                  </S.Grid>
                </S.GridScroll>
              </>
            ) : (
              <S.ListViewContainer>
                {listAgendaGroups.length === 0 ? (
                  <S.ListEmptyHint>
                    Nenhuma data de avaliação com início ou término neste mês. Altere o
                    mês ou o filtro de curso, ou volte à visualização em grade (Mês).
                  </S.ListEmptyHint>
                ) : (
                  listAgendaGroups.map(([dateKey, dayEvents]) => (
                    <S.ListDayBlock
                      key={dateKey}
                      type="button"
                      aria-label={`Abrir atividades de ${moment(dateKey, 'YYYY-MM-DD').format('D [de] MMMM [de] YYYY')}`}
                      onClick={() =>
                        setDayModal({ dateKey, events: dayEvents })
                      }
                    >
                      <S.ListDayHeader>
                        {moment(dateKey, 'YYYY-MM-DD').format(
                          'dddd, D [de] MMMM [de] YYYY',
                        )}
                        {dateKey === todayKey ? ' · Hoje' : ''}
                      </S.ListDayHeader>
                      <S.ModalEventList>
                        {dayEvents.map((ev) => (
                          <S.ModalEventItem key={ev.id} $kind={ev.kind}>
                            <S.ModalEventKind $kind={ev.kind}>
                              {ev.kind === 'start' ? 'Início' : 'Término'}
                            </S.ModalEventKind>
                            <S.ModalEventTitle>{ev.title}</S.ModalEventTitle>
                            {ev.disciplineName ? (
                              <S.ModalEventMeta>
                                Disciplina: {ev.disciplineName}
                              </S.ModalEventMeta>
                            ) : null}
                            {ev.courseName ? (
                              <S.ModalEventMeta>Curso: {ev.courseName}</S.ModalEventMeta>
                            ) : null}
                          </S.ModalEventItem>
                        ))}
                      </S.ModalEventList>
                    </S.ListDayBlock>
                  ))
                )}
              </S.ListViewContainer>
            )}
          </S.CalendarCard>
        )}
      </S.Content>

      <Dialog
        open={dayModal !== null}
        onClose={() => setDayModal(null)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="calendar-day-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 3,
            m: 2,
            ...(calendarDark && theme.colors
              ? {
                  bgcolor: theme.colors.gray07,
                  color: theme.colors.text.primary,
                  backgroundImage: 'none',
                  border: `1px solid ${theme.colors.gray03}`,
                }
              : {}),
          },
        }}
      >
        <DialogTitle
          id="calendar-day-dialog-title"
          sx={{
            pb: 0.5,
            pr: 6,
            position: 'relative',
            ...(calendarDark && theme.colors
              ? {
                  color: theme.colors.text.primary,
                  borderBottomColor: theme.colors.gray03,
                }
              : {}),
          }}
        >
          {modalMoment?.isValid() ? (
            <>
              <S.ModalTitleRow>
                <S.ModalTitleDay>{modalMoment.date()}</S.ModalTitleDay>
                <S.ModalTitleWeekday>
                  {modalMoment.format('ddd').replace(/\.$/, '').toUpperCase()}.
                </S.ModalTitleWeekday>
                {dayModal?.dateKey === todayKey ? (
                  <S.ModalTodayBadge>· Hoje</S.ModalTodayBadge>
                ) : null}
              </S.ModalTitleRow>
              <S.ModalDateSub>
                {modalMoment.format('D [de] MMMM [de] YYYY')}
              </S.ModalDateSub>
            </>
          ) : null}
          <IconButton
            aria-label="Fechar"
            onClick={() => setDayModal(null)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              ...(calendarDark && theme.colors
                ? { color: theme.colors.text.secondary }
                : {}),
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            pt: 2,
            ...(calendarDark && theme.colors
              ? {
                  borderColor: theme.colors.gray03,
                  color: theme.colors.text.primary,
                }
              : {}),
          }}
        >
          {!dayModal || dayModal.events.length === 0 ? (
            <S.ModalEmptyHint>
              Nenhuma avaliação com início ou término neste dia. Escolha outro dia na grade
              para ver as atividades.
            </S.ModalEmptyHint>
          ) : (
            <S.ModalEventList>
              {dayModal.events.map((ev) => (
                <S.ModalEventItem key={ev.id} $kind={ev.kind}>
                  <S.ModalEventKind $kind={ev.kind}>
                    {ev.kind === 'start' ? 'Início' : 'Término'}
                  </S.ModalEventKind>
                  <S.ModalEventTitle>{ev.title}</S.ModalEventTitle>
                  {ev.disciplineName ? (
                    <S.ModalEventMeta>Disciplina: {ev.disciplineName}</S.ModalEventMeta>
                  ) : null}
                  {ev.courseName ? (
                    <S.ModalEventMeta>Curso: {ev.courseName}</S.ModalEventMeta>
                  ) : null}
                </S.ModalEventItem>
              ))}
            </S.ModalEventList>
          )}
        </DialogContent>
      </Dialog>
    </S.Container>
  );
}
